// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId } from 'react';

import { useCanEdit } from '../../../app/CaseRoleContext';
import type { SagenRow } from '../../../bygning/overblik';
import { SAGSDATA_GROUPS } from '../../../bygning/sagsdata';
import { Spinner } from '../../Spinner';
import { TextLink } from '../../TextLink';
import { Panel } from '../Panel';
import styles from './Overblik.module.css';

export interface SagenPanelProps {
  /** The case name (the record's `name`); empty when there is no record yet. */
  name: string;
  /** `sagenRows(project, extras)`. */
  rows: readonly SagenRow[];
  /** The case store's first load has landed. */
  loaded: boolean;
  /** Some values exist only in this browser (an older server, contract gap 3). */
  held: boolean;
  /** Opens the Sagsdata editor (`?edit=sag`). */
  onEdit: () => void;
}

/**
 * "Sagen": the case record, read-only, grouped as the editor's fieldsets
 * (Sag, Bygning, Personer, Nedrivning · BEK 496, Noter) with spacing alone
 * between them. A filled field shows its value; an empty one the report needs
 * says "mangler"; any other empty field is left out. "Rediger" opens the
 * Sagsdata editor as a dialog — for an editor only.
 */
export function SagenPanel({ name, rows, loaded, held, onEdit }: SagenPanelProps) {
  const canEdit = useCanEdit();
  const id = useId();
  const headingId = `${id}-h`;
  const shown = rows.filter((r) => r.value !== null || r.required);
  const groups = SAGSDATA_GROUPS.map((g) => ({ ...g, rows: shown.filter((r) => r.group === g.id) })).filter((g) => g.rows.length > 0);

  return (
    <Panel
      title="Sagen"
      headingId={headingId}
      fill
      action={
        canEdit && (
          <TextLink size="sm" nowrap aria-haspopup="dialog" aria-describedby={headingId} onClick={onEdit}>
            Rediger
          </TextLink>
        )
      }
    >
      {!loaded ? (
        <Spinner label="Indlæser sagen…" />
      ) : (
        <>
          {name.trim() ? (
            <p className={styles.caseName}>{name}</p>
          ) : (
            <p className={styles.muted}>Ingen sagsoplysninger endnu</p>
          )}
          <div className={styles.sagenGroups}>
            {groups.map((g) => (
              <dl key={g.id} className={styles.list} aria-label={g.title}>
                {g.rows.map((r) => (
                  <div key={r.key} className={r.key === 'notes' ? `${styles.row} ${styles.rowStacked}` : styles.row}>
                    <dt className={styles.rowLabel}>{r.label}</dt>
                    <dd className={`${styles.rowValue} ${styles.rowWrap}`}>
                      {r.value ?? <span className={styles.missing}>mangler</span>}
                      {r.note && <span className={r.tone ? `${styles.rowNote} ${styles[r.tone]}` : styles.rowNote}>{r.note}</span>}
                    </dd>
                  </div>
                ))}
              </dl>
            ))}
          </div>
          {held && <p className={styles.foot}>Nogle felter er kun gemt i denne browser — ikke gemt af serveren endnu.</p>}
        </>
      )}
    </Panel>
  );
}
